import classNames from 'classnames';
import { get, map } from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';

import Button from 'components/Button';
import { SecondaryHeading } from 'components/Headings';
import MediaGallery from 'components/MediaGallery';
import RichText from 'components/RichText';
import { largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import TransitionObserver from 'utils/TransitionObserver';

import { TABS_HEIGHT } from './constants';

class ShowcaseRowsDesktop extends PureComponent {
  constructor(props) {
    super(props);
    this.rowRefs = map(props.rows, () => React.createRef());
    this.state = { activeMediaGalleryIndex: 0 };
    this.transitionObserver = new TransitionObserver(this.rowRefs, props.handleRowTransition);
  }

  componentDidMount() {
    this.transitionObserver.observe();
  }

  componentWillUnmount() {
    this.transitionObserver.unobserve();
  }

  setActiveMediaGalleryIndex(index) {
    this.setState({ activeMediaGalleryIndex: index });
  }

  render() {
    const { rows, activeRowIndex } = this.props;

    const reducedImages = map(rows, ({ desktopImages, desktopImage }) => ({
      desktopImages,
      desktopImage,
    }));

    return (
      <div className="ShowcaseRowsDesktop">
        <div className="ShowcaseRowsDesktop__rows">
          {map(rows, (content, _index) => {
            return (
              <div
                className="ShowcaseRowsDesktop__copy-section"
                ref={this.rowRefs[_index]}
                id={`row-${_index}`}
                key={`row-${_index}`}
              >
                <div className="ShowcaseRowsDesktop__copy-section-inner">
                  <SecondaryHeading>{content.heading}</SecondaryHeading>
                  <RichText content={content.subtext} />
                  <div className="ShowcaseRowsDesktop__cta-links">
                    {map(content.ctaLinks, ({ text, url, openInNewWindow }, index) => (
                      <div className="ShowcaseRowsDesktop__cta-link" key={index}>
                        <Button linkTo={url} target={openInNewWindow && '_blank'} isFullWidth>
                          {text}
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="ShowcaseRowsDesktop__gallery-wrapper">
          {map(reducedImages, ({ desktopImages, desktopImage }, index) => (
            <div
              className={classNames('ShowcaseRowsDesktop__gallery', {
                'ShowcaseRowsDesktop__gallery--visible': index <= activeRowIndex,
              })}
              key={get(desktopImage, ['file', 'cloudinaryPublicId'])}
            >
              <MediaGallery
                media={desktopImages || [desktopImage]}
                onIndexChange={_index => this.setActiveMediaGalleryIndex(_index)}
                activeIndex={this.state.activeMediaGalleryIndex}
                isResponsive
              />
            </div>
          ))}
        </div>

        <style jsx>
          {`
            .ShowcaseRowsDesktop {
              display: flex;
            }

            .ShowcaseRowsDesktop__rows {
              flex: 1;
            }

            .ShowcaseRowsDesktop__rows > :nth-child(even) {
              background-color: ${Theme.get('colors-grey')};
            }

            .ShowcaseRowsDesktop__gallery-wrapper {
              flex: 1;
              position: relative;
              position: sticky;
              top: ${Theme.get('top-navigation-height') + TABS_HEIGHT}px;
              left: 0;
              height: calc(100vh - ${Theme.get('top-navigation-height') + TABS_HEIGHT}px);
              background-color: ${Theme.get('colors-grey')};
            }

            .ShowcaseRowsDesktop__gallery {
              position: absolute;
              top: 0;
              bottom: 0;
              left: 0;
              right: 0;

              pointer-events: none;
              opacity: 0;
              transition: opacity ${Theme.get('transition-duration-ms')}
                ${Theme.get('transition-timing-ease-in-out')};
            }

            .ShowcaseRowsDesktop__gallery--visible {
              pointer-events: auto;
              opacity: 1;
            }

            .ShowcaseRowsDesktop__copy-section {
              display: flex;
              flex-direction: column;
              justify-content: center;
              padding: 0 17% 0 ${Theme.get('spacing') * 5}px;
              min-height: calc(100vh - ${Theme.get('top-navigation-height') + TABS_HEIGHT}px);
            }

            .ShowcaseRowsDesktop__cta-links {
              display: flex;
              flex-flow: row wrap;
              width: 100%;
            }

            .ShowcaseRowsDesktop__cta-link {
              width: 100%;
              padding-bottom: ${Theme.get('spacing') * 4}px;
            }

            @media ${largeUp} {
              .ShowcaseRowsDesktop__cta-link {
                width: 50%;
              }

              .ShowcaseRowsDesktop__cta-links > :first-child {
                padding-right: ${Theme.get('spacing') * 2}px;
              }

              .ShowcaseRowsDesktop__cta-links > :last-child {
                padding-left: ${Theme.get('spacing') * 2}px;
              }
            }
          `}
        </style>
        <style jsx global>
          {`
            .ShowcaseRowsDesktop .MediaGallery {
              height: 100%;
            }

            .ShowcaseRowsDesktop .ShowcaseRowsDesktop__copy-section .UnorderedList {
              margin-bottom: ${Theme.get('spacing') * 6}px;
            }

            .ShowcaseRowsDesktop__copy-section .UnorderedList .Paragraph {
              margin: 0;
            }

            .ShowcaseRowsDesktop__copy-section .typography--secondary-heading {
              margin-bottom: ${Theme.get('spacing') * 2}px;
            }
          `}
        </style>
      </div>
    );
  }
}

ShowcaseRowsDesktop.propTypes = {
  activeRowIndex: PropTypes.number.isRequired,
  rows: PropTypes.arrayOf(
    PropTypes.shape({
      heading: PropTypes.string.isRequired,
      subtext: PropTypes.shape({}).isRequired,
      ctaLinks: PropTypes.arrayOf(
        PropTypes.shape({
          _contentType: PropTypes.string.isRequired,
          internalName: PropTypes.string.isRequired,
          text: PropTypes.string.isRequired,
          url: PropTypes.string.isRequired,
          openInNewWindow: PropTypes.bool.isRequired,
        })
      ),
      desktopImages: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.shape({
            cloudinaryPublicId: PropTypes.string,
            details: PropTypes.shape({
              image: PropTypes.shape({
                height: PropTypes.number,
                width: PropTypes.number,
              }),
            }),
          }),
        })
      ),
      desktopImage: PropTypes.shape({
        file: PropTypes.shape({
          cloudinaryPublicId: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      }),
      mobileImages: PropTypes.arrayOf(
        PropTypes.shape({
          file: PropTypes.shape({
            cloudinaryPublicId: PropTypes.string,
            details: PropTypes.shape({
              image: PropTypes.shape({
                height: PropTypes.number,
                width: PropTypes.number,
              }),
            }),
          }),
        })
      ),
      mobileImage: PropTypes.shape({
        file: PropTypes.shape({
          cloudinaryPublicId: PropTypes.string,
          details: PropTypes.shape({
            image: PropTypes.shape({
              height: PropTypes.number,
              width: PropTypes.number,
            }),
          }),
        }),
      }),
    })
  ).isRequired,
  handleRowTransition: PropTypes.func.isRequired,
};

export default ShowcaseRowsDesktop;
